import { useCallback, useEffect, useRef } from 'react';
import { isIosDevice, isMobileTabViewport } from './iosViewport';

const MOBILE_KBD_OPEN_MIN_PX = 48;
const MOBILE_KBD_INSET_MAX_RATIO = 0.52;

function computeMobileKeyboardState(vv, baselineH, isIOS) {
    const layoutH = window.innerHeight;
    const visibleH = vv.height;
    const offsetTop = vv.offsetTop || 0;
    if (!Number.isFinite(layoutH) || !Number.isFinite(visibleH)) {
        return { inset: 0, open: false };
    }
    const base = Number.isFinite(baselineH) && baselineH > 0 ? baselineH : layoutH;
    const shrinkVV = layoutH - visibleH - offsetTop;
    const shrinkLayout = layoutH - visibleH;
    const shrinkFromBaseline = Math.max(base - layoutH, base - visibleH, 0);
    const shrink = Math.max(shrinkVV, shrinkLayout, shrinkFromBaseline, 0);

    if (shrink < MOBILE_KBD_OPEN_MIN_PX) {
        return { inset: 0, open: false };
    }

    if (isIOS) {
        const cap = Math.round(layoutH * MOBILE_KBD_INSET_MAX_RATIO);
        const inset = Math.min(Math.round(shrinkLayout), cap);
        return { inset, open: true };
    }

    const inset = Math.max(0, Math.round(shrink));
    return { inset, open: true };
}

function isMobileFocusableField(el) {
    if (!el || typeof el.tagName !== 'string') return false;
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag === 'INPUT') {
        const type = (el.type || 'text').toLowerCase();
        return !['button', 'checkbox', 'radio', 'file', 'hidden', 'submit', 'reset'].includes(type);
    }
    return false;
}

export function useMobileKeyboardAvoidance({
    active,
    modalRef,
    composerRef,
    scrollFocusedField = false,
}) {
    const baselineRef = useRef(0);

    const captureBaseline = useCallback(() => {
        if (composerRef?.current?.contains(document.activeElement)) return;
        const vv = window.visualViewport;
        if (!vv) return;
        const layoutH = window.innerHeight;
        const visibleH = vv.height;
        const shrink = Math.max(layoutH - visibleH - (vv.offsetTop || 0), layoutH - visibleH, 0);
        if (shrink >= MOBILE_KBD_OPEN_MIN_PX) return;
        baselineRef.current = Math.max(layoutH, visibleH);
    }, [composerRef]);

    useEffect(() => {
        if (!active) {
            baselineRef.current = 0;
            return undefined;
        }
        if (!isMobileTabViewport()) return undefined;

        const modal = modalRef?.current;
        if (!modal) return undefined;

        const vv = window.visualViewport;
        if (!vv) return undefined;

        const isIOS = isIosDevice();
        let raf = 0;
        let scrollRaf = 0;

        const resetStyles = () => {
            modal.style.height = '';
            modal.style.marginTop = '';
            modal.style.setProperty('--kbd-inset', '0px');
            modal.style.removeProperty('--task-vvh');
            modal.style.removeProperty('--task-vv-top');
            modal.style.removeProperty('--task-browser-bottom');
            modal.style.removeProperty('--task-composer-h');
            delete modal.dataset.kbdOpen;
        };

        const applyLayout = () => {
            const composerFocused = composerRef?.current?.contains(document.activeElement);
            let { open: kbdOpen } = computeMobileKeyboardState(vv, baselineRef.current, isIOS);
            if (composerFocused) kbdOpen = true;

            if (kbdOpen) {
                const vvH = Math.max(1, Math.round(vv.height));
                const vvTop = Math.max(0, Math.round(vv.offsetTop || 0));
                const composerH = Math.ceil(
                    composerRef?.current?.getBoundingClientRect?.().height || 96
                );

                modal.style.height = `${vvH}px`;
                modal.style.marginTop = `${vvTop}px`;

                modal.style.setProperty('--task-vvh', `${vvH}px`);
                modal.style.setProperty('--task-vv-top', `${vvTop}px`);
                modal.style.setProperty('--task-composer-h', `${composerH}px`);
                modal.style.setProperty('--task-browser-bottom', '0px');

                modal.dataset.kbdOpen = 'true';
                return;
            }

            resetStyles();
        };

        const scheduleLayout = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(applyLayout);
        };

        const onVvScroll = () => {
            if (!isIOS) {
                scheduleLayout();
                return;
            }
            cancelAnimationFrame(scrollRaf);
            scrollRaf = requestAnimationFrame(scheduleLayout);
        };

        const scrollFieldIntoView = (target) => {
            if (!scrollFocusedField || !isMobileFocusableField(target)) return;
            window.setTimeout(() => {
                try {
                    target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                } catch {
                    target.scrollIntoView(true);
                }
            }, 120);
        };

        const onFocusIn = (e) => {
            if (!modal.contains(e.target)) return;
            if (!composerRef?.current?.contains(e.target) && !scrollFocusedField) return;
            if (scrollFocusedField && !isMobileFocusableField(e.target)) return;
            scheduleLayout();
            window.setTimeout(scheduleLayout, 80);
            window.setTimeout(scheduleLayout, 220);
            scrollFieldIntoView(e.target);
        };

        const onFocusOut = (e) => {
            if (!modal.contains(e.target)) return;
            scheduleLayout();
            const related = e.relatedTarget;
            if (related && modal.contains(related)) return;
            window.setTimeout(() => {
                if (composerRef?.current?.contains(document.activeElement)) return;
                captureBaseline();
                scheduleLayout();
            }, 120);
            window.setTimeout(() => {
                if (composerRef?.current?.contains(document.activeElement)) return;
                captureBaseline();
                scheduleLayout();
            }, 360);
        };

        scheduleLayout();
        const t0 = window.setTimeout(() => {
            captureBaseline();
            scheduleLayout();
        }, 0);

        vv.addEventListener('resize', scheduleLayout);
        vv.addEventListener('scroll', onVvScroll);
        window.addEventListener('resize', scheduleLayout);
        window.addEventListener('orientationchange', () => {
            captureBaseline();
            scheduleLayout();
        });
        modal.addEventListener('focusin', onFocusIn);
        modal.addEventListener('focusout', onFocusOut, true);

        return () => {
            window.clearTimeout(t0);
            vv.removeEventListener('resize', scheduleLayout);
            vv.removeEventListener('scroll', onVvScroll);
            window.removeEventListener('resize', scheduleLayout);
            modal.removeEventListener('focusin', onFocusIn);
            modal.removeEventListener('focusout', onFocusOut, true);
            cancelAnimationFrame(raf);
            cancelAnimationFrame(scrollRaf);
            resetStyles();
        };
    }, [active, modalRef, composerRef, scrollFocusedField, captureBaseline]);
}
